import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MapPin, Palette, Waves, ForkKnife, MusicNotes, BuildingApartment, ArrowRight, PlayCircle, X, Compass } from "@phosphor-icons/react";

const CATEGORIES = [
  { k: "attractions", label: "Attractions", Icon: Palette, emoji: "🏛️" },
  { k: "beaches", label: "Beaches", Icon: Waves, emoji: "🏝️" },
  { k: "restaurants", label: "Restaurants", Icon: ForkKnife, emoji: "🍽️" },
  { k: "nightclubs", label: "Nightclubs", Icon: MusicNotes, emoji: "🎶" },
  { k: "hotels", label: "Hotels", Icon: BuildingApartment, emoji: "🏨" },
];

export const DiscoverLiberia = () => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("attractions");
  const [loading, setLoading] = useState(true);
  const [showIntro, setShowIntro] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/discover/liberia")
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;
  const active = data[tab];
  const activeCat = CATEGORIES.find(c => c.k === tab);
  if (!active) return null;

  const bookTour = (item) => {
    if (!active.tour_lookup_key) return;
    const notes = encodeURIComponent(`Interested in ${item.name} (${item.location}). ${item.desc}`);
    nav(`/book/concierge/${active.tour_lookup_key}?notes=${notes}`);
  };

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 border-t border-white/10" data-testid="discover-liberia">
      {showIntro && <LiberiaIntroModal onClose={() => setShowIntro(false)} onBook={() => { setShowIntro(false); nav("/book/concierge/concierge_discover_tour"); }} />}
      {/* Hero banner with founder photo backdrop */}
      <div
        data-testid="discover-hero-banner"
        className="relative overflow-hidden rounded-[2.5rem] border border-[#D4AF37]/30 shadow-[0_30px_120px_-40px_rgba(212,175,55,0.35)] mb-12"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[380px]">
          <div className="lg:col-span-7 relative bg-gradient-to-br from-[#0B0B0D] via-[#121214] to-[#0B0B0D] p-8 sm:p-12 flex flex-col justify-center">
            <div className="eyebrow gold-text mb-4">🇱🇷 Discover Liberia · Escorted Experiences</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
              Explore the <span className="gold-text italic">coast</span>.<br />
              Live the culture.
            </h2>
            <p className="mt-5 text-zinc-300 max-w-xl leading-relaxed">
              From turquoise shores at Robertsport to Providence Island, Ducor Palace, and Monrovia
              nightlife — every experience is escorted by a RideXelite host who knows the country.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowIntro(true)}
                data-testid="discover-watch-intro-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#F3E5AB] transition"
              >
                <PlayCircle weight="fill" size={16} /> Liberia in 30 seconds
              </button>
              <button
                onClick={() => nav("/book/concierge/concierge_discover_tour")}
                data-testid="discover-book-concierge-tour-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white text-sm hover:border-[#D4AF37]/60 transition"
              >
                Book escorted tour <ArrowRight size={14} weight="bold" />
              </button>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs uppercase tracking-[0.25em] text-zinc-500">
              <span>🌊 Beaches</span>
              <span className="text-[#D4AF37]/50">·</span>
              <span>🏛️ Attractions</span>
              <span className="text-[#D4AF37]/50">·</span>
              <span>🍽️ Cuisine</span>
              <span className="text-[#D4AF37]/50">·</span>
              <span>🎶 Nightlife</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative min-h-[280px] lg:min-h-[380px]">
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_ridexelite-platform/artifacts/si7abj9b_20260723_012814-ANIMATION.gif"
              alt="Discover Liberia — coast to culture"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#0B0B0D] via-[#0B0B0D]/60 to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0B0B0D]/80 to-transparent pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
        <div>
          <div className="eyebrow gold-text mb-3">🇱🇷 Discover Liberia</div>
          <h2 className="font-serif-display text-4xl lg:text-5xl text-white leading-tight">
            {active.title}
          </h2>
          <p className="mt-3 text-zinc-400">{active.subtitle}</p>
        </div>
        {active.tour_lookup_key && (
          <button
            onClick={() => nav(`/book/concierge/${active.tour_lookup_key}`)}
            data-testid="discover-book-tour-btn"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#F3E5AB] transition"
          >
            Book escorted experience <ArrowRight size={14} weight="bold" />
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="discover-tabs">
        {CATEGORIES.map(c => {
          const on = tab === c.k;
          return (
            <button
              key={c.k}
              onClick={() => setTab(c.k)}
              data-testid={`discover-tab-${c.k}`}
              className={`px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 transition ${
                on
                  ? "bg-[#D4AF37] text-black border border-[#D4AF37]"
                  : "border border-white/10 text-zinc-300 hover:border-[#D4AF37]/40 hover:text-white"
              }`}
            >
              <c.Icon size={14} weight="duotone" /> {c.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="discover-grid">
        {active.items.map((it, i) => (
          <div
            key={it.name}
            className="group rounded-2xl border border-white/10 bg-[#121214] p-6 hover:border-[#D4AF37]/40 transition-all duration-300 hover:-translate-y-1 flex flex-col"
            data-testid={`discover-item-${tab}-${i}`}
          >
            <div className="flex items-start justify-between mb-4">
              <span className="text-xs uppercase tracking-[0.25em] text-zinc-500 group-hover:text-[#D4AF37] transition">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-lg" aria-hidden>{activeCat?.emoji}</span>
            </div>
            <h3 className="font-serif-display text-xl text-white leading-tight">{it.name}</h3>
            <div className="mt-2 flex items-center gap-1.5 text-xs text-[#D4AF37]">
              <MapPin size={12} weight="fill" /> {it.location}
            </div>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed flex-1">{it.desc}</p>
            {active.tour_lookup_key && (
              <button
                onClick={() => bookTour(it)}
                data-testid={`discover-book-${tab}-${i}`}
                className="mt-5 w-full py-2.5 rounded-full text-xs font-medium border border-white/10 text-zinc-300 hover:border-[#D4AF37] hover:text-[#D4AF37] transition inline-flex items-center justify-center gap-1.5"
              >
                Book escorted visit <ArrowRight size={12} weight="bold" />
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default DiscoverLiberia;


const HIGHLIGHTS = [
  { icon: "🏝️", title: "Coast", body: "560 km of Atlantic shoreline — Robertsport surf, CeCe Beach, Kenema, and Marshall's palm-lined coves." },
  { icon: "🏛️", title: "History", body: "Founded 1847. Providence Island, Ducor Palace, Waterside Market — Africa's oldest republic." },
  { icon: "🍽️", title: "Cuisine", body: "Cassava leaf, palm butter, jollof, pepper soup, fresh grilled snapper — bold flavors, generous plates." },
  { icon: "🎶", title: "Hospitality", body: "Warm welcomes, live music, and hipco beats that light up Sinkor & Monrovia after dark." },
];

function LiberiaIntroModal({ onClose, onBook }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      data-testid="liberia-intro-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 bg-black/80 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl rounded-[2rem] overflow-hidden border border-[#D4AF37]/40 shadow-[0_40px_120px_-30px_rgba(212,175,55,0.5)]"
      >
        {/* Backdrop animation */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_ridexelite-platform/artifacts/si7abj9b_20260723_012814-ANIMATION.gif"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#09090B]/95 via-[#0B0B0D]/80 to-[#09090B]/95" />
        </div>

        <div className="relative p-8 sm:p-12">
          <button
            onClick={onClose}
            data-testid="liberia-intro-close"
            aria-label="Close intro"
            className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition"
          >
            <X size={18} weight="bold" />
          </button>

          <div className="flex items-center gap-2 mb-4">
            <Compass weight="duotone" size={16} className="text-[#D4AF37]" />
            <div className="eyebrow gold-text">🇱🇷 Liberia · in 30 seconds</div>
          </div>
          <h3 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-3xl">
            The <span className="gold-text italic">little corner of Africa</span> that stole a century of hearts.
          </h3>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-2xl leading-relaxed">
            Four decades of quiet reinvention. Warm people. Wild coastlines. Every RideXelite tour is
            hosted by a local who lives, works, and eats where you'll be exploring.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {HIGHLIGHTS.map((h) => (
              <div
                key={h.title}
                className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl leading-none" aria-hidden>{h.icon}</div>
                  <div>
                    <div className="text-[#D4AF37] text-xs uppercase tracking-[0.22em] mb-1">{h.title}</div>
                    <div className="text-sm text-zinc-200 leading-relaxed">{h.body}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={onBook}
              data-testid="liberia-intro-book-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#F3E5AB] transition"
            >
              Book an escorted tour <ArrowRight size={14} weight="bold" />
            </button>
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white text-sm hover:border-white/40 transition"
            >
              Continue browsing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
